import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatInlineChart from "./ChatInlineChart";

export function MarkdownBlock({ text }) {
  return (
    <div style={{ lineHeight: 1.6 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ children, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer">
              {children}
            </a>
          ),
          table: ({ children, ...props }) => (
            <div style={{ overflowX: "auto", marginBottom: 12 }}>
              <table
                {...props}
                className="table is-bordered is-striped is-narrow is-fullwidth"
              >
                {children}
              </table>
            </div>
          ),
          code: ({ inline, children, ...props }) => {
            if (inline) {
              return (
                <code
                  {...props}
                  style={{
                    background: "#f5f5f5",
                    padding: "0.1rem 0.25rem",
                    borderRadius: 4,
                  }}
                >
                  {children}
                </code>
              );
            }
            return (
              <pre
                style={{
                  background: "#f5f5f5",
                  padding: 12,
                  borderRadius: 6,
                  overflowX: "auto",
                  marginTop: 8,
                }}
              >
                <code {...props}>{children}</code>
              </pre>
            );
          },
        }}
      >
        {text || ""}
      </ReactMarkdown>
    </div>
  );
}

const CHART_MARKER_RE = /\{\{CHART:(\w+)\}\}/g;

// Charts auto-injected when data exists but LLM omitted the marker
const AUTO_INJECT_CHARTS = [
  "cdi_summary", "drought_gauge", "drought_trend",
  "place_cdi", "place_spi", "place_fapar", "place_rainfall",
];

const DATA_KEY_MAP = {
  cdi_summary: "cdi_summary",
  drought_gauge: "cdi_summary",
  drought_trend: "cdi_summary",
  place_cdi: "cdi_series",
  place_spi: "spi_series",
  place_fapar: "fapar_series",
  place_rainfall: "rainfall_vs_average",
};

export default function ChartAwareMarkdown({ text, chartData }) {
  if (!text) return null;

  const segments = [];
  const emittedKeys = new Set();
  let lastIndex = 0;
  let match;
  const re = new RegExp(CHART_MARKER_RE.source, "g");

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: "chart", key: match[1] });
    emittedKeys.add(match[1]);
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }

  // Prepend auto-inject charts the LLM didn't emit markers for
  const missing = AUTO_INJECT_CHARTS.filter((k) => !emittedKeys.has(k));
  if (missing.length > 0 && chartData) {
    const inject = missing
      .filter((k) => {
        const dataKey = DATA_KEY_MAP[k] || k;
        const d = chartData[dataKey];
        return d && (!Array.isArray(d) || d.length > 0);
      })
      .map((k) => ({ type: "chart", key: k }));
    if (inject.length > 0) {
      segments.splice(0, 0, ...inject);
    }
  }

  return (
    <>
      {segments.map((seg, i) =>
        seg.type === "chart" ? (
          <ChatInlineChart key={`chart-${i}`} chartKey={seg.key} chartData={chartData} />
        ) : (
          <MarkdownBlock key={`text-${i}`} text={seg.content} />
        )
      )}
    </>
  );
}
